import { useState, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  where,
  startAfter,
  DocumentSnapshot,
  Timestamp,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { EvaluationListItem, EvaluationDetail, EvaluationFilters } from "../types";
import { mapToListItem, mapToDetail } from "./evaluationMappers";

const PAGE_SIZE = 20;

interface UseEvaluationsReturn {
  evaluations: EvaluationListItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  fetchEvaluations: (filters?: EvaluationFilters) => Promise<void>;
  loadMore: () => Promise<void>;
}

/**
 * 評価一覧取得フック
 */
export const useEvaluations = (): UseEvaluationsReturn => {
  const [evaluations, setEvaluations] = useState<EvaluationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [currentFilters, setCurrentFilters] = useState<EvaluationFilters>({});

  const fetchEvaluations = useCallback(async (filters: EvaluationFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      setCurrentFilters(filters);

      const constraints: QueryConstraint[] = [];

      // v1.3.0以降のみ取得（topic.idが存在するもの）
      constraints.push(where("topic.id", "!=", ""));

      // 難易度フィルター
      if (filters.difficulty) {
        constraints.push(where("topic.difficulty", "==", filters.difficulty));
      }

      // 日付フィルター
      if (filters.startDate) {
        constraints.push(where("timestamp", ">=", Timestamp.fromDate(filters.startDate)));
      }
      if (filters.endDate) {
        // 終了日は翌日の0時まで含める
        const endDate = new Date(filters.endDate);
        endDate.setDate(endDate.getDate() + 1);
        constraints.push(where("timestamp", "<", Timestamp.fromDate(endDate)));
      }

      // ソートとリミット
      constraints.push(orderBy("timestamp", "desc"));
      constraints.push(limit(PAGE_SIZE));

      const q = query(collection(db, "analyticsEvaluations"), ...constraints);
      const snapshot = await getDocs(q);

      const evaluationList = snapshot.docs.map((docSnap) =>
        mapToListItem(docSnap.id, docSnap.data())
      );

      setEvaluations(evaluationList);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching evaluations:", err);
      setError(err instanceof Error ? err.message : "評価データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!lastDoc || !hasMore || loading) return;

    try {
      setLoading(true);

      const constraints: QueryConstraint[] = [];

      // v1.3.0以降のみ取得（topic.idが存在するもの）
      constraints.push(where("topic.id", "!=", ""));

      // 難易度フィルター
      if (currentFilters.difficulty) {
        constraints.push(where("topic.difficulty", "==", currentFilters.difficulty));
      }

      // 日付フィルター
      if (currentFilters.startDate) {
        constraints.push(where("timestamp", ">=", Timestamp.fromDate(currentFilters.startDate)));
      }
      if (currentFilters.endDate) {
        const endDate = new Date(currentFilters.endDate);
        endDate.setDate(endDate.getDate() + 1);
        constraints.push(where("timestamp", "<", Timestamp.fromDate(endDate)));
      }

      // ソート、カーソル、リミット
      constraints.push(orderBy("timestamp", "desc"));
      constraints.push(startAfter(lastDoc));
      constraints.push(limit(PAGE_SIZE));

      const q = query(collection(db, "analyticsEvaluations"), ...constraints);
      const snapshot = await getDocs(q);

      const moreEvaluations = snapshot.docs.map((docSnap) =>
        mapToListItem(docSnap.id, docSnap.data())
      );

      setEvaluations((prev) => [...prev, ...moreEvaluations]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error loading more evaluations:", err);
      setError(err instanceof Error ? err.message : "追加データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [lastDoc, hasMore, loading, currentFilters]);

  return {
    evaluations,
    loading,
    error,
    hasMore,
    fetchEvaluations,
    loadMore,
  };
};

interface UseEvaluationDetailReturn {
  evaluation: EvaluationDetail | null;
  rawEvaluationData: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
  fetchEvaluation: (evaluationId: string) => Promise<void>;
}

/**
 * 評価詳細取得フック
 */
export const useEvaluationDetail = (): UseEvaluationDetailReturn => {
  const [evaluation, setEvaluation] = useState<EvaluationDetail | null>(null);
  const [rawEvaluationData, setRawEvaluationData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvaluation = useCallback(async (evaluationId: string) => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, "analyticsEvaluations", evaluationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError("評価データが見つかりません");
        setEvaluation(null);
        setRawEvaluationData(null);
        return;
      }

      // 生データはマッピング前に保持する（マッピングに失敗しても生JSONは確認できるように）
      setRawEvaluationData({ id: docSnap.id, ...docSnap.data() });

      const evaluationDetail = mapToDetail(docSnap.id, docSnap.data());
      setEvaluation(evaluationDetail);
    } catch (err) {
      console.error("Error fetching evaluation detail:", err);
      setError(err instanceof Error ? err.message : "評価詳細の取得に失敗しました");
      // 直前に開いていた評価が残っていると、エラー表示の下に別レコードの内容が並んでしまう。
      // 生データは保持したままにして、未対応バージョンでも生JSONは確認できるようにする。
      setEvaluation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    evaluation,
    rawEvaluationData,
    loading,
    error,
    fetchEvaluation,
  };
};
